import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useI18n } from "@/i18n";
import { useAuth } from "@/auth";
import { api } from "@/lib/api";
import Logo from "@/components/Logo";
import BrandToolbar from "@/components/BrandToolbar";
import { Lock, ArrowLeft, Mail, User, Loader2, Eye, EyeOff } from "lucide-react";

function formatApiError(detail, fallback) {
  if (detail == null) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function Login() {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const { user, loading, refresh } = useAuth();
  const [mode, setMode] = useState("email"); // "email" | "google"
  const [isSignup, setIsSignup] = useState(() => {
    // Allow opening signup tab via ?signup=1 (used by /register redirect).
    try {
      return new URLSearchParams(window.location.search).get("signup") === "1";
    } catch (_) {
      return false;
    }
  });
  const [showPwd, setShowPwd] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [consent, setConsent] = useState({
    terms: false,
    privacy: false,
    marketing: false,
  });
  // B5 — Honeypot. Real users never see/touch this; bots that auto-fill
  // form inputs will populate it and the backend will silently swallow it.
  const [honeypot, setHoneypot] = useState("");
  // Turnstile — only rendered when REACT_APP_ENABLE_TURNSTILE=true and key set.
  const TURNSTILE_ENABLED = (process.env.REACT_APP_ENABLE_TURNSTILE || "false").toLowerCase() === "true";
  const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";
  const [turnstileToken, setTurnstileToken] = useState("");

  // Single-source-of-truth for the post-login navigation. Both the useEffect
  // (user-already-authenticated case) and handleSubmit (fresh login case)
  // share this ref so they cannot fire twice and race against each other.
  const didRedirectRef = useRef(false);
  // True when the user is arriving from email confirmation. We capture this
  // ONCE on mount (and from sessionStorage as a backup) because by the time
  // useEffect fires after a successful login, window.location.search may
  // already have changed and the redirect race would lose ?confirmed=1.
  const postConfirmRef = useRef(false);
  useEffect(() => {
    try {
      const fromUrl = new URLSearchParams(window.location.search).get("confirmed") === "1";
      const fromSs = sessionStorage.getItem("ks_post_confirm") === "1";
      if (fromUrl || fromSs) {
        postConfirmRef.current = true;
        sessionStorage.setItem("ks_post_confirm", "1");
      }
    } catch (_) { /* noop */ }
  }, []);

  const consumePostConfirm = () => {
    const v = postConfirmRef.current;
    try { sessionStorage.removeItem("ks_post_confirm"); } catch (_) { /* noop */ }
    postConfirmRef.current = false;
    return v;
  };

  useEffect(() => {
    if (!loading && user && !didRedirectRef.current) {
      didRedirectRef.current = true;
      const isFreeOrEmpty = !user.plan || user.plan === "free";
      const target = (postConfirmRef.current && isFreeOrEmpty) ? "/plans?welcome=1" : "/dashboard";
      if (target === "/plans?welcome=1") consumePostConfirm();
      navigate(target, { replace: true });
    }
  }, [user, loading, navigate]);

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    // Mandatory consent gates (signup only)
    if (isSignup && !consent.terms) {
      setError(t("consent_terms_required"));
      return;
    }
    if (isSignup && !consent.privacy) {
      setError(t("consent_privacy_required"));
      return;
    }
    setBusy(true);
    try {
      const url = isSignup ? "/auth/register" : "/auth/login";
      const body = isSignup
        ? {
            email: form.email.trim(),
            password: form.password,
            name: form.name.trim(),
            lang,
            terms_accepted: true,
            privacy_accepted: true,
            marketing_consent: !!consent.marketing,
            legal_version: "v1.0-2026-05-25",
            website: honeypot,  // B5 honeypot
            turnstile_token: turnstileToken,  // B5 Turnstile (ignored when flag off)
          }
        : { email: form.email.trim(), password: form.password };
      await api.post(url, body);
      // Cross-site cookies need a microtask to be persisted by Safari before
      // the next request can read them. Without this tiny gap the immediate
      // /auth/me below sometimes raced ahead of the cookie store and the
      // user saw "Ha ocurrido un error" right after a successful login.
      await new Promise((r) => setTimeout(r, 60));
      // Verify session was actually established. We rely on AuthContext's
      // refresh() — it already calls /auth/me and stores the user. No need
      // for a second redundant /auth/me right after.
      const me = await refresh();
      if (!me || (!me.user_id && !me.email)) {
        setError(t("auth_session_lost"));
        setIsSignup(false);
        setBusy(false);
        return;
      }
      toast.success(isSignup ? t("auth_created") : t("auth_welcome"));
      // Post-confirmation flow (Change 3 — Iter 22): if the user just confirmed
      // their email and is still on the free plan, walk them through
      // /plans?welcome=1 so they can apply a discount (e.g. BETA100) before
      // reaching the dashboard. didRedirectRef ensures the useEffect below
      // does NOT fire a second navigate that would overwrite this one.
      const isFreeOrEmpty = !me.plan || me.plan === "free";
      const target = (postConfirmRef.current && isFreeOrEmpty) ? "/plans?welcome=1" : "/dashboard";
      if (target === "/plans?welcome=1") consumePostConfirm();
      didRedirectRef.current = true;
      navigate(target, { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail, t("auth_generic_error")) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen-safe bg-obsidian text-white flex flex-col">
      <header className="glass-header pt-safe-or-5">
        <div className="max-w-7xl mx-auto px-3 sm:px-8 py-3 sm:py-5 flex items-center justify-between gap-2">
          <Link to="/" className="flex items-center gap-2 text-muted hover:text-gold-light shrink-0">
            <ArrowLeft className="w-4 h-4" />
            <Logo size={24} />
          </Link>
          <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
            <BrandToolbar />
          </div>
        </div>
      </header>

      <main className="flex-1 flex items-start sm:items-center justify-center px-4 sm:px-6 py-6 sm:py-12 pb-safe-or-3">
        <div className="glass-card rounded-md p-6 sm:p-8 md:p-12 max-w-md w-full gold-glow fade-in">
          <Lock className="w-8 sm:w-10 h-8 sm:h-10 text-gold mx-auto mb-4 sm:mb-6" strokeWidth={1.2} />
          <h1 className="font-display text-3xl sm:text-4xl mb-2 text-center">
            {isSignup ? t("login_create") : t("login_title")}
          </h1>
          <p className="text-muted mb-6 sm:mb-8 font-body text-center text-sm">
            {isSignup
              ? t("login_create_sub")
              : t("login_sub")}
          </p>

          {/* Email-confirmed banner (Bug 2 — usuario llegó tras click en email) */}
          {(() => {
            try {
              const params = new URLSearchParams(window.location.search);
              if (params.get("confirmed") === "1") {
                return (
                  <div
                    className="mb-5 rounded-md border border-emerald-500/40 px-4 py-3 text-sm text-emerald-200"
                    style={{ background: "rgba(16,185,129,0.10)" }}
                    data-testid="login-email-confirmed-banner"
                  >
                    ✓ {t("login_email_confirmed_banner")}
                  </div>
                );
              }
            } catch (_) { /* SSR safety */ }
            return null;
          })()}

          {/* Method tabs */}
          <div className="flex gap-1 border-b border-gold-subtle mb-2 -mt-2">
            <button
              type="button"
              onClick={() => setMode("email")}
              className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-label transition border-b-2 -mb-px ${
                mode === "email"
                  ? "text-gold border-gold"
                  : "text-faded border-transparent hover:text-muted"
              }`}
              data-testid="login-tab-email"
            >
              <Mail className="w-3.5 h-3.5" /> {t("login_tab_email")}
            </button>
            <button
              type="button"
              onClick={() => setMode("google")}
              className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-label transition border-b-2 -mb-px ${
                mode === "google"
                  ? "text-gold border-gold"
                  : "text-faded border-transparent hover:text-muted"
              }`}
              data-testid="login-tab-google"
            >
              <User className="w-3.5 h-3.5" /> Google
            </button>
          </div>
          {mode === "email" && (
            <p className="text-[11px] text-faded text-center mb-5" data-testid="login-any-email-hint">
              {t("login_any_email_hint")}
            </p>
          )}
          {mode === "google" && (
            <div className="mb-5" />
          )}

          {mode === "email" ? (
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="email-auth-form">
              {isSignup && (
                <div>
                  <label className="font-label text-faded block mb-2">{t("auth_name")}</label>
                  <input
                    className="input-field"
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    data-testid="signup-name"
                  />
                </div>
              )}
              <div>
                <label className="font-label text-faded block mb-2">{t("auth_email")}</label>
                <input
                  type="email"
                  className="input-field"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="auth-email"
                  autoComplete="email"
                />
              </div>
              <div>
                <label className="font-label text-faded block mb-2">{t("auth_password")}</label>
                <div className="relative">
                  <input
                    type={showPwd ? "text" : "password"}
                    className="input-field pr-12"
                    required
                    minLength={isSignup ? 8 : undefined}
                    placeholder="••••••••"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    data-testid="auth-password"
                    autoComplete={isSignup ? "new-password" : "current-password"}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd((v) => !v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-gold hover:text-gold-light transition rounded"
                    data-testid="auth-password-toggle"
                    aria-label={showPwd ? t("auth_hide_password") : t("auth_show_password")}
                    tabIndex={-1}
                  >
                    {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {isSignup && (
                  <p className="text-xs text-faded mt-1">{t("auth_password_hint")}</p>
                )}
                {!isSignup && (
                  <Link
                    to="/forgot-password"
                    className="text-xs text-gold-light hover:underline mt-2 inline-block"
                    data-testid="forgot-password-link"
                  >
                    {t("auth_forgot")}
                  </Link>
                )}
              </div>

              {error && (
                <div
                  className="text-sm text-red-400 bg-red-900/20 border border-red-900/30 rounded px-3 py-2"
                  data-testid="auth-error"
                >
                  {error}
                </div>
              )}

              {isSignup && (
                <div className="space-y-2 pt-2 text-xs text-gold-light/90">
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={consent.terms}
                      onChange={(e) =>
                        setConsent((c) => ({ ...c, terms: e.target.checked }))
                      }
                      className="accent-gold mt-0.5 w-4 h-4 flex-shrink-0"
                      data-testid="consent-terms"
                      required
                    />
                    <span className="leading-snug">
                      {t("consent_terms_label")}{" "}
                      <Link
                        to="/legal/terminos"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gold hover:underline"
                      >
                        {t("consent_terms_link")}
                      </Link>{" "}
                      <span className="text-red-400">*</span>
                    </span>
                  </label>
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={consent.privacy}
                      onChange={(e) =>
                        setConsent((c) => ({
                          ...c,
                          privacy: e.target.checked,
                        }))
                      }
                      className="accent-gold mt-0.5 w-4 h-4 flex-shrink-0"
                      data-testid="consent-privacy"
                      required
                    />
                    <span className="leading-snug">
                      {t("consent_privacy_label")}{" "}
                      <Link
                        to="/legal/privacidad"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gold hover:underline"
                      >
                        {t("consent_privacy_link")}
                      </Link>{" "}
                      <span className="text-red-400">*</span>
                    </span>
                  </label>
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={consent.marketing}
                      onChange={(e) =>
                        setConsent((c) => ({
                          ...c,
                          marketing: e.target.checked,
                        }))
                      }
                      className="accent-gold mt-0.5 w-4 h-4 flex-shrink-0"
                      data-testid="consent-marketing"
                    />
                    <span className="leading-snug text-faded">
                      {t("consent_marketing_label")}
                    </span>
                  </label>
                </div>
              )}

              {/* B5 — Honeypot. Visually hidden, NOT display:none (some bots
                  skip display:none). Real users will not see or focus it. */}
              {isSignup && (
                <div
                  aria-hidden="true"
                  style={{
                    position: "absolute",
                    left: "-10000px",
                    top: "auto",
                    width: "1px",
                    height: "1px",
                    overflow: "hidden",
                  }}
                >
                  <label htmlFor="website">Website</label>
                  <input
                    type="text"
                    id="website"
                    name="website"
                    tabIndex={-1}
                    autoComplete="off"
                    value={honeypot}
                    onChange={(e) => setHoneypot(e.target.value)}
                  />
                </div>
              )}

              {/* Turnstile widget — only rendered when flag enabled.
                  Activation instructions live in PRELAUNCH_CHECKLIST.md */}
              {isSignup && TURNSTILE_ENABLED && TURNSTILE_SITE_KEY && (
                <div
                  className="cf-turnstile"
                  data-sitekey={TURNSTILE_SITE_KEY}
                  data-callback="onTurnstileSuccess"
                  data-testid="turnstile-widget"
                  ref={(el) => {
                    if (!el) return;
                    // Lazy-load the Turnstile script once
                    if (!document.getElementById("cf-turnstile-script")) {
                      const s = document.createElement("script");
                      s.id = "cf-turnstile-script";
                      s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js";
                      s.async = true;
                      s.defer = true;
                      document.head.appendChild(s);
                    }
                    // Wire global callback
                    window.onTurnstileSuccess = (token) => setTurnstileToken(token);
                  }}
                />
              )}

              <button
                type="submit"
                disabled={busy}
                className="btn-gold w-full justify-center mt-2"
                data-testid="auth-submit"
              >
                {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                {isSignup ? t("auth_signup_btn") : t("auth_login_btn")}
              </button>

              <button
                type="button"
                onClick={() => {
                  setIsSignup(!isSignup);
                  setError(null);
                }}
                className="text-xs text-faded hover:text-gold-light w-full text-center mt-4"
                data-testid="toggle-signup"
              >
                {isSignup ? t("auth_have_account") : t("auth_no_account")}
              </button>
            </form>
          ) : (
            <div className="space-y-4">
              <button
                onClick={handleGoogle}
                className="btn-gold w-full justify-center"
                data-testid="login-google-btn"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#0A0A0A"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#0A0A0A" opacity=".7"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#0A0A0A" opacity=".5"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" fill="#0A0A0A" opacity=".3"/>
                </svg>
                {t("auth_continue_google")}
              </button>
            </div>
          )}

          <p className="mt-8 text-xs text-faded font-label text-center">{t("login_secure")}</p>
          <p className="mt-3 text-xs text-faded text-center">
            {t("auth_need_help")} <a href="mailto:support@keepsoul.app" className="text-gold-light hover:underline">support@keepsoul.app</a>
          </p>
        </div>
      </main>
    </div>
  );
}
